import { useState, useEffect } from 'react'
import { Form, Button, Row, Col } from 'react-bootstrap'
import { useQuery } from 'react-query'
import api from '../../services/api'
import { addDaysToDateOnly, getCurrentYearMx, getTodayDateOnlyMx, startOfLocalDayFromApi } from '../../utils/apiDateFormat'

const BudgetForm = ({ budget, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    cliente: '',
    tipo: 'anual',
    año: getCurrentYearMx(),
    fecha_inicio: getTodayDateOnlyMx(),
    fecha_fin: addDaysToDateOnly(getTodayDateOnlyMx(), 365),
    estado: 'borrador',
    responsable: '',
    monto_total: 0
  })

  const [errors, setErrors] = useState({})

  // Obtener clientes
  const { data: clients } = useQuery(
    'clients-for-budget',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )

  // Obtener usuarios para responsable
  const { data: users } = useQuery(
    'users-for-budget',
    async () => {
      const response = await api.get('/auth/users/')
      return response.data.results || response.data || []
    }
  )

  useEffect(() => {
    if (budget) {
      setFormData({
        nombre: budget.nombre || '',
        cliente: budget.cliente || budget.cliente_id || '',
        tipo: budget.tipo || 'anual',
        año: budget.año || getCurrentYearMx(),
        fecha_inicio: budget.fecha_inicio || getTodayDateOnlyMx(),
        fecha_fin: budget.fecha_fin || addDaysToDateOnly(getTodayDateOnlyMx(), 365),
        estado: budget.estado || 'borrador',
        responsable: budget.responsable || budget.responsable_id || '',
        monto_total: budget.monto_total || 0
      })
    }
  }, [budget])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const newErrors = {}
    if (!formData.nombre) newErrors.nombre = 'Nombre es requerido'
    if (!formData.cliente) newErrors.cliente = 'Cliente es requerido'
    if (!formData.fecha_inicio) newErrors.fecha_inicio = 'Fecha de inicio es requerida'
    if (!formData.fecha_fin) newErrors.fecha_fin = 'Fecha de fin es requerida'
    
    const fin = startOfLocalDayFromApi(formData.fecha_fin)
    const inicio = startOfLocalDayFromApi(formData.fecha_inicio)
    if (fin && inicio && fin < inicio) {
      newErrors.fecha_fin = 'La fecha de fin debe ser posterior a la fecha de inicio'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    const submitData = { ...formData }
    
    if (budget) {
      submitData.id = budget.id
    }
    
    onSubmit(submitData)
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Nombre del Presupuesto *</Form.Label>
            <Form.Control
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              isInvalid={!!errors.nombre}
              required
            />
            {errors.nombre && <Form.Control.Feedback type="invalid">{errors.nombre}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Cliente *</Form.Label>
            <Form.Select
              name="cliente"
              value={formData.cliente}
              onChange={handleChange}
              isInvalid={!!errors.cliente}
              required
            >
              <option value="">Seleccionar cliente...</option>
              {clients?.map(client => (
                <option key={client.id} value={client.id}>
                  {client.razon_social || client.business_name || `${client.nombre} ${client.apellido_paterno}`}
                </option>
              ))}
            </Form.Select>
            {errors.cliente && <Form.Control.Feedback type="invalid">{errors.cliente}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Tipo de Presupuesto *</Form.Label>
            <Form.Select
              name="tipo"
              value={formData.tipo}
              onChange={handleChange}
              required
            >
              <option value="anual">Anual</option>
              <option value="proyecto">Proyecto</option>
              <option value="departamento">Departamento</option>
              <option value="otro">Otro</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Año *</Form.Label>
            <Form.Control
              type="number"
              name="año"
              value={formData.año}
              onChange={handleChange}
              min="2000"
              max="2100"
              required
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Estado</Form.Label>
            <Form.Select
              name="estado"
              value={formData.estado}
              onChange={handleChange}
            >
              <option value="borrador">Borrador</option>
              <option value="en_aprobacion">En Aprobación</option>
              <option value="vigente">Vigente</option>
              <option value="cerrado">Cerrado</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Fecha de Inicio *</Form.Label>
            <Form.Control
              type="date"
              name="fecha_inicio"
              value={formData.fecha_inicio}
              onChange={handleChange}
              isInvalid={!!errors.fecha_inicio}
              required
            />
            {errors.fecha_inicio && <Form.Control.Feedback type="invalid">{errors.fecha_inicio}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Fecha de Fin *</Form.Label>
            <Form.Control
              type="date"
              name="fecha_fin"
              value={formData.fecha_fin}
              onChange={handleChange}
              isInvalid={!!errors.fecha_fin}
              required
            />
            {errors.fecha_fin && <Form.Control.Feedback type="invalid">{errors.fecha_fin}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Responsable</Form.Label>
            <Form.Select
              name="responsable"
              value={formData.responsable}
              onChange={handleChange}
            >
              <option value="">Seleccionar responsable...</option>
              {users?.map(user => (
                <option key={user.id} value={user.id}>
                  {user.username} {user.email ? `(${user.email})` : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Monto Total</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              name="monto_total"
              value={formData.monto_total}
              onChange={handleChange}
              readOnly
              className="bg-light"
            />
            <Form.Text className="text-muted">
              El monto total se calcula automáticamente desde las líneas del presupuesto
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Guardando...' : (budget ? 'Actualizar' : 'Crear')}
        </Button>
      </div>
    </Form>
  )
}

export default BudgetForm

